/**
 * Service Account API Service
 *
 * API methods for service account operations including creation, updates, and deletion.
 * Service accounts are non-human identities for app job execution context.
 */

import apiClient from './client';
import type { ApiSuccessResponse, ApiCollectionResponse, ApiActionResponse } from '../types/api.types';
import type {
  ServiceAccountSanitized,
  ServiceAccountCreateDTO,
  ServiceAccountUpdateDTO,
  ServiceAccountListQuery,
} from '../types/service-account.types';

// ============================================================================
// SERVICE ACCOUNT SERVICE
// ============================================================================

export const serviceAccountService = {
  /**
   * List all service accounts for a tenant
   */
  listServiceAccounts: async (tenantId: string, query?: ServiceAccountListQuery): Promise<ServiceAccountSanitized[]> => {
    const response = await apiClient.get<ApiCollectionResponse<ServiceAccountSanitized>>(`/tenants/${tenantId}/service-accounts`, {
      params: query,
    });
    return response.data.data;
  },

  /**
   * Get service account by ID
   */
  getServiceAccount: async (tenantId: string, serviceAccountId: string): Promise<ServiceAccountSanitized> => {
    const response = await apiClient.get<ApiSuccessResponse<{ serviceAccount: ServiceAccountSanitized }>>(`/tenants/${tenantId}/service-accounts/${serviceAccountId}`);
    return response.data.data.serviceAccount;
  },

  /**
   * Get service account by code
   */
  getServiceAccountByCode: async (tenantId: string, serviceAccountCode: string): Promise<ServiceAccountSanitized> => {
    const response = await apiClient.get<ApiSuccessResponse<{ serviceAccount: ServiceAccountSanitized }>>(`/tenants/${tenantId}/service-accounts/by-code/${serviceAccountCode}`);
    return response.data.data.serviceAccount;
  },

  /**
   * Create a new service account
   */
  createServiceAccount: async (tenantId: string, data: ServiceAccountCreateDTO): Promise<ServiceAccountSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<{ serviceAccount: ServiceAccountSanitized }>>(`/tenants/${tenantId}/service-accounts`, data);
    return response.data.data.serviceAccount;
  },

  /**
   * Update a service account
   */
  updateServiceAccount: async (tenantId: string, serviceAccountId: string, data: ServiceAccountUpdateDTO): Promise<ServiceAccountSanitized> => {
    const response = await apiClient.put<ApiSuccessResponse<{ serviceAccount: ServiceAccountSanitized }>>(`/tenants/${tenantId}/service-accounts/${serviceAccountId}`, data);
    return response.data.data.serviceAccount;
  },

  /**
   * Delete a service account
   */
  deleteServiceAccount: async (tenantId: string, serviceAccountId: string): Promise<void> => {
    await apiClient.delete<ApiActionResponse>(`/tenants/${tenantId}/service-accounts/${serviceAccountId}`);
  },
};

export default serviceAccountService;
